// Talks to the "CLS Portal - ATLAS Letters" Chrome extension (tools/atlas-letters-extension).
// The extension's portal-bridge.js answers window.postMessage calls on the portal's pages;
// it opens ATLAS in its own window, waits for the person to sign in, and sends the letters back.

import { clientStorage } from "./clientStorage.js";

const FROM_PAGE = "cls-portal";
const FROM_EXTENSION = "atlas-letters-extension";

const post = (type, extra = {}) => window.postMessage({ source: FROM_PAGE, type, ...extra }, window.location.origin);

function listen(handler) {
  const onMessage = (event) => {
    if (event.source !== window || event.origin !== window.location.origin) return;
    if (event.data?.source !== FROM_EXTENSION) return;
    handler(event.data);
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

// The bridge says "ready" when it loads, which can be before or after the page asks, so
// remember any "ready" seen since this module loaded as well as asking.
let seenVersion = null;
if (typeof window !== "undefined") {
  listen((message) => {
    if (message.type === "ready") seenVersion = message.version || "";
  });
}

// Resolves to { version } when the extension is installed on this browser, or null.
// Asks every half second, because the extension's script can start after the page's.
export function detectAtlasExtension(timeoutMs = 5000) {
  if (seenVersion !== null) return Promise.resolve({ version: seenVersion });
  return new Promise((resolve) => {
    const finish = (found) => {
      stop();
      clearInterval(asking);
      clearTimeout(timer);
      resolve(found);
    };
    const stop = listen((message) => {
      if (message.type === "ready") finish({ version: message.version || "" });
    });
    const asking = setInterval(() => post("atlas-letters:ping"), 500);
    const timer = setTimeout(() => finish(null), timeoutMs);
    post("atlas-letters:ping");
  });
}

// Opens the ATLAS window and resolves with { letters, requisitions, skipped, warnings } once the person
// has signed in and the letters are read. onProgress gets { stage, message, done, total }.
export function fetchAtlasLetters({ since = "", onProgress = () => {} } = {}) {
  return new Promise((resolve, reject) => {
    const stop = listen((message) => {
      if (message.type === "progress") onProgress(message);
      if (message.type === "result") {
        stop();
        resolve({
          letters: message.letters || [],
          requisitions: message.requisitions || [],
          skipped: message.skipped || [],
          warnings: message.warnings || [],
        });
      }
      if (message.type === "error") {
        stop();
        reject(new Error(message.message || "The ATLAS download stopped."));
      }
    });
    post("atlas-letters:start", { since });
  });
}

// Tells the extension these letters are now in the Aid Planner, so the next run skips them.
export function markAtlasLettersImported(keys = []) {
  if (keys.length) post("atlas-letters:imported", { keys });
}

export function atlasLetterToFile(letter) {
  const binary = atob(letter.base64 || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], letter.fileName || "ATLAS letter.pdf", { type: "application/pdf" });
}

// Letters that open a file (an approval or the submitted application) may start a new
// matter; a tax invoice or refusal for a client the Aid Planner doesn't know is only listed.
export function atlasLetterMayStartMatter(payload) {
  const type = payload?.fields?.documentType || "";
  return type === "grant_approval_letter" || type === "application_request";
}

// ATLAS lists clients as "SURNAME, GIVEN NAMES"; the Aid Planner keeps "Given Names Surname".
export function atlasClientName(client = "") {
  const text = String(client || "").replace(/\s+/g, " ").trim();
  const [surname, given] = text.includes(",") ? text.split(/,\s*/, 2) : ["", text];
  const titleCase = (part) => part.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (match, lead, letter) => lead + letter.toUpperCase());
  return [given, surname].filter(Boolean).map(titleCase).join(" ");
}

const lastRunKey = (brand) => `atlas-letters:last-run:${brand || "portal"}`;

export function readLastAtlasRun(brand) {
  try { return clientStorage.getItem(lastRunKey(brand)) || ""; } catch { return ""; }
}

export function saveLastAtlasRun(brand, day) {
  try { clientStorage.setItem(lastRunKey(brand), day); } catch { /* storage blocked */ }
}

// The default "received since" date: the day of the last run, else a week ago.
export function defaultAtlasSince(brand, today = new Date()) {
  const last = readLastAtlasRun(brand);
  if (last) return last;
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  return localDay(weekAgo);
}

export function localDay(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
